/*****************************************************
Naam: GeofenceNotification.jsx
Locatie: src/components/Dashboard/GeofenceNotification.jsx
Datum: 2026-03-25
Versie: 1.0.0
******************************************************
Beschrijving:
Toast-stijl notificaties voor GEOfencing hub passages.
Toont bij ENTER en EXIT events van GPS trackers.
Verdwijnt automatisch na 10 seconden, max 5 tegelijk.
******************************************************/

import React, { useState, useEffect, useCallback } from 'react';

const HUB_TYPE_ICON = {
  'Centrale hub': '⭐',
  'Distributiecentrum': '🏭',
  'Healthcare hub': '🏥',
  'Regionaal': '📍',
};

function GeofenceToast({ event, onClose }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Fade in
    requestAnimationFrame(() => setVisible(true));
    // Auto close na 10 seconden
    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 300);
    }, 10000);
    return () => clearTimeout(t);
  }, [onClose]);

  const isEnter = event.type === 'ENTER';
  const icon = HUB_TYPE_ICON[event.hub?.type] || '📍';
  const tijdstip = new Date(event.timestamp).toLocaleTimeString('nl-NL', {
    hour: '2-digit', minute: '2-digit'
  });

  return (
    <div style={{
      background: 'white',
      border: `2px solid ${isEnter ? '#059669' : '#d97706'}`,
      borderRadius: '10px',
      padding: '12px 14px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      minWidth: '300px',
      maxWidth: '360px',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateX(0)' : 'translateX(30px)',
      transition: 'all 0.3s ease',
      position: 'relative',
      cursor: 'default',
    }}>
      {/* Sluit knop */}
      <button onClick={() => { setVisible(false); setTimeout(onClose, 300); }} style={{
        position: 'absolute', top: '8px', right: '10px',
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: '14px', color: '#9ca3af', lineHeight: 1, padding: 0,
      }}>✕</button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <span style={{ fontSize: '20px' }}>{icon}</span>
        <div>
          <div style={{
            fontSize: '13px', fontWeight: '700',
            color: isEnter ? '#059669' : '#d97706',
          }}>
            {isEnter ? '📥 Hub bereikt' : '📤 Hub verlaten'} — {event.hub?.naam}
          </div>
          <div style={{ fontSize: '11px', color: '#6b7280' }}>
            {event.hub?.stad}{event.hub?.land ? `, ${event.hub.land}` : ''} · {tijdstip}
          </div>
        </div>
      </div>

      {/* Aanvraag info */}
      {event.aanvraag && (
        <div style={{
          background: '#f9fafb', borderRadius: '6px', padding: '7px 10px',
          fontSize: '12px', color: '#374151', lineHeight: 1.5,
        }}>
          <span style={{ fontWeight: '600' }}>📦 {event.aanvraag.id}</span>
          <span style={{ color: '#9ca3af' }}> · </span>
          {event.aanvraag.van} → {event.aanvraag.naar}
        </div>
      )}

      {/* Afstand */}
      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '5px' }}>
        Afstand tot hub: {event.afstand_km} km · Straal: {event.hub?.straal_km || '—'} km
      </div>

      {/* Progress bar (loopt leeg in 10 sec) */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px',
        background: '#e5e7eb', borderRadius: '0 0 8px 8px', overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          background: isEnter ? '#059669' : '#d97706',
          animation: 'geofence-timer 10s linear forwards',
        }} />
      </div>
    </div>
  );
}

export default function GeofenceNotification({ events, onDismiss }) {
  return (
    <>
      <style>{`
        @keyframes geofence-timer {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
      <div style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        pointerEvents: 'none',
      }}>
        {events.map(ev => (
          <div key={ev.id} style={{ pointerEvents: 'all' }}>
            <GeofenceToast event={ev} onClose={() => onDismiss(ev.id)} />
          </div>
        ))}
      </div>
    </>
  );
}
